import { computed, reactive, ComputedRef } from 'vue'
import { AttributeFilter, FilterEntry } from '../common/interfaces'
import { FilterType, WallSelectionType } from '@/constants'
import { ISubject, Note, Subject } from '../common/classes'

const _state = reactive({
  filterCollection: {
    tags: { name: 'tags', list: [] },
    notags: { name: 'notags', list: [] },
    users: { name: 'users', list: [] },
    boards: { name: 'boards', list: [] },
    highlights: { name: 'highlights', list: [] },
    time: { name: 'time', list: [] },
  } as Record<string, AttributeFilter>,

  wallFilterCollection: {
    updated: { name: 'updated', list: [] },
    time: { name: 'time', list: [] },
  } as Record<string, AttributeFilter>,

  selectedFilterId: FilterType.TIME as string, // time as default
  selectedWallFilterId: FilterType.UPDATE as string, // time as default
  wallSelection: WallSelectionType.ALL as string,
  selectedBoardId: '' as string, // sub-wall id to filter pinned contributions wall-based
  selectedTaskId: '' as string, // task id to filter pinned contributions task-based

  filteredNotes: [] as Note[], // filtered contributions -> search base
  subjectFilter: {} as Subject | ISubject,
})

interface State {
  filterCollection: Record<string, AttributeFilter>
  wallFilterCollection: Record<string, AttributeFilter>
  selectedFilterId: string
  selectedWallFilterId: string
  wallSelection: string
  selectedBoardId: string
  selectedTaskId: string
  filteredNotes: Note[]
  subjectFilter: Subject | ISubject
}

interface Getters {
  filterCollection: ComputedRef<Record<string, AttributeFilter>>
  wallFilterCollection: ComputedRef<Record<string, AttributeFilter>>
  selectedFilterId: ComputedRef<string>
  selectedWallFilterId: ComputedRef<string>
  wallSelection: ComputedRef<string>
  selectedBoardId: ComputedRef<string>
  selectedTaskId: ComputedRef<string>
  filteredNotes: ComputedRef<Note[]>
  subjectFilter: ComputedRef<Subject | ISubject>
}
interface Actions {
  setFilterType: (filterId: string) => void
  setWallFilterType: (filterId: string) => void
  setWallSelection: (selection: string) => void
  setSubjectFilter: (subject: Subject | ISubject) => void
  setBoardId: (id: string) => void
  setTaskId: (taskId: string) => void
  sortFilterEntries: () => FilterEntry[]
  setFilteredNotes: (notes: Note[]) => void
  resetFilteredNotes: () => void
  resetAllFilters: () => void
  addFilterEntry: (id: string, ids: string[], filterId: string) => void
  resetSubjectFilter: () => void
}

interface ServiceInterface {
  state: State
  actions: Actions
  getters: Getters
}

function useFilterStore(): ServiceInterface {
  const actions = {
    setWallFilterType: function (filterId: string) {
      _state.selectedWallFilterId = filterId
    },

    setFilterType: function (filterId: string) {
      _state.selectedFilterId = filterId
    },

    setWallSelection: function (selection: string) {
      _state.wallSelection = selection
    },

    setSubjectFilter: function (subject: Subject | ISubject) {
      _state.subjectFilter = subject
    },

    resetSubjectFilter: function () {
      _state.subjectFilter = {} as Subject
    },

    setBoardId: function (id: string) {
      _state.selectedBoardId = id
    },

    setTaskId: function (taskId: string) {
      _state.selectedTaskId = taskId
    },

    setFilteredNotes: function (notes: Note[]) {
      for (const note of notes) {
        if (_state.filteredNotes.findIndex((fc) => fc._id === note._id) === -1) {
          _state.filteredNotes.push(note)
        }
      }
    },

    resetFilteredNotes: function (): void {
      _state.filteredNotes = []
    },

    /**
     * resetAllFilters()
     *
     * invoked on task change -> all notes must be reset
     */
    resetAllFilters: function () {
      _state.filterCollection = {
        tags: { name: 'tags', list: [] },
        notags: { name: 'notags', list: [] },
        users: { name: 'users', list: [] },
        boards: { name: 'boards', list: [] },
        highlights: { name: 'highlights', list: [] },
        time: { name: 'time', list: [] },
      }

      _state.wallFilterCollection = {
        update: { name: 'update', list: [] },
        time: { name: 'time', list: [] },
      }
    },

    /**
     * addFilterEntry()
     *
     * adds a filter entry for a given filterId
     * each filter entry stores a list of notes related to that entry
     *
     * entries can be added as part of fetching data initially or as part of an entity update
     * @param id
     * @param ids
     * @param filterId
     */
    addFilterEntry: async function (id: string, ids: string[], filterId: string): Promise<void> {
      const createEntry = () => {
        // create new entry
        const entry = { id: id, noteIds: new Set(ids) } // id: e.g. board id / name, noteIds: ids of notes for that board
        _state.filterCollection[filterId].list.push(entry)
      }

      try {
        const index = _state.filterCollection[filterId].list.findIndex((item) => item.id === id)
        if (index !== -1) {
          // update existing entry
          ids.map((id) => {
            _state.filterCollection[filterId].list[index].noteIds.add(id)
          })
        } else {
          createEntry()
        }
      } catch (e) {
        // no entry found (no list)
        createEntry()
      }
    },

    sortFilterEntries: function (): FilterEntry[] {
      let sortingFunction = this.sortFilterEntriesByName(false)
      switch (_state.selectedFilterId) {
        case 'tags':
          break
        case 'users':
          sortingFunction = this.sortFilterEntriesByName(false)
          break
        case 'boards':
          sortingFunction = this.sortFilterEntriesByName(false)
          break
        case 'highlights':
          break
        case 'time':
          sortingFunction = this.sortFilterEntriesByTime(true)
          break
      }
      return _state.filterCollection[_state.selectedFilterId].list.sort(sortingFunction)
    },

    sortFilterEntriesByTime: function (desc: boolean) {
      const result = desc ? -1 : 1
      return (c: FilterEntry, n: FilterEntry) => {
        return new Date(c.id) > new Date(n.id) ? result : -result
      }
    },

    sortFilterEntriesByName: function (desc: boolean) {
      const result = desc ? -1 : 1
      return (c: FilterEntry, n: FilterEntry) => {
        return c.id.charCodeAt(0) > n.id.charCodeAt(0) ? result : -result
      }
    },
  }

  const getters = {
    get filterCollection(): ComputedRef<Record<string, AttributeFilter>> {
      return computed(() => _state.filterCollection)
    },

    get wallFilterCollection(): ComputedRef<Record<string, AttributeFilter>> {
      return computed(() => _state.wallFilterCollection)
    },

    get wallSelection(): ComputedRef<string> {
      return computed(() => _state.wallSelection)
    },

    get selectedFilterId(): ComputedRef<string> {
      return computed(() => _state.selectedFilterId)
    },

    get selectedWallFilterId(): ComputedRef<string> {
      return computed(() => _state.selectedWallFilterId)
    },

    get selectedBoardId(): ComputedRef<string> {
      return computed(() => _state.selectedBoardId)
    },

    get selectedTaskId(): ComputedRef<string> {
      return computed(() => _state.selectedTaskId)
    },

    get filteredNotes(): ComputedRef<Note[]> {
      return computed(() => _state.filteredNotes)
    },

    get subjectFilter(): ComputedRef<Subject | ISubject> {
      return computed(() => _state.subjectFilter)
    },
  }

  const state = _state

  return { state, actions, getters }
}

export default useFilterStore
